import { ModalDialog } from "@/components/data-display/modal-dialog";
import { Input } from "@/components/inputs/input";
import { Button } from "@/components/ui/button";
import { yupResolver } from "@hookform/resolvers/yup";
import { ChangeEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TrashIcon } from "@heroicons/react/24/outline";
import { formatCurrency } from "@/utils/format/currency";
import { getSpaceTakeValue } from "@/utils/get-space-take-value/get-space-take-value";
import {
  calculateCreditPricing,
  DEFAULT_SPACE_TAKE_PCT,
  FIXED_CREDIT_PRICE,
} from "../../pricing/marginCalculator";
import { getBitlyShortUrl } from "@/utils/get-short-url";
import { useAuth } from "@/features/authentication";
import { apiHubspotIntegrationMs } from "@/services/api-hubspot-integration-ms";
import { createToast } from "vercel-toast";
import { Info } from "lucide-react";
import { Card, FilterValues } from "../../types";
import { useSelection } from "../../contexts/SelectionContext";
import * as yup from "yup";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { reportError } from "@/utils/observability";
import { visionService } from "@/services/vision-service";
import { Skeleton } from "@/components/ui/skeleton";

const ProposalPrivateRoomSchema = yup.object().shape({
  deal_url: yup
    .string()
    .required("Deal ID é obrigatório")
    .test("not-empty", "Deal ID é obrigatório", (value) => {
      return value !== null && value !== undefined && value.trim() !== "";
    }),
  briefing: yup.string().optional(),
  extra_value: yup
    .number()
    .optional()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError("O valor deve ser um número")
    .min(0, "O valor extra deve ser positivo"),
});

type ProposalPrivateRoomFormData = yup.InferType<
  typeof ProposalPrivateRoomSchema
>;

export type ProposalItem = {
  id: string;
  search_id?: number | null;
  label: string;
  space: {
    id: string;
    name: string;
    neighborhood: string;
    cityname: string;
  };
  capacity: number;
  size: number | null;
  monthly_price: number;
  credits: number;
  take: number;
  client_price?: number | null;
  extra_credits?: number | null;
  total_price?: number | null;
  total_credits?: number | null;
  margin?: number | null;
  extra_value?: number | null;
  space_id?: string | null;
  private_room_id?: string | null;
  match_percent?: string | null;
  monthly_credit_price?: number | null;
  dpp?: string | null;
  status?: number | string | null;
  days_since_update?: string | null;
  distance_km?: number | null;
  amenities?: string | null;
  details?: {
    id: string | number | null;
    search_id?: number | null;
    space_id?: string | number | null;
    label?: string | null;
    size?: number | null;
    capacity?: number | null;
    monthly_price?: number | null;
    yearly_price?: number | null;
    amenities?: string | null;
    extra_services?: string | null;
    shared_amenities?: string | null;
    shared_extra_services?: string | null;
    advantages?: string | null;
    picture_url?: string | null;
    picture_url1?: string | null;
    picture_url2?: string | null;
    picture_url3?: string | null;
    picture_url4?: string | null;
    picture_url5?: string | null;
    notes?: string | null;
    status?: number | null;
    created_at?: string | null;
    updated_at?: string | null;
    deleted_at?: string | null;
    created_by_user_id?: number | null;
  } | null;
  space_details?: {
    id: string | number;
    name?: string;
    neighborhood?: string | null;
    cityname?: string | null;
    address?: string | null;
    latitude?: string | null;
    longitude?: string | null;
    enabled?: boolean | null;
    type?: number | null;
    acknowledge_date?: string | null;
  } | null;
  detailed_score_analysis?: Array<{
    criterion: string;
    requested: any;
    offered: any;
    matched: boolean;
  }> | null;
  score_details?: any;
};

export const calculateValues = (baseItem: ProposalItem, extraValue = 0) => {
  const values = calculateCreditPricing(
    baseItem.monthly_price,
    baseItem.take,
    extraValue,
  );

  return {
    ...baseItem,
    client_price: values.clientPrice,
    extra_credits: values.extraCredits,
    total_price: values.totalPrice,
    total_credits: values.totalCredits,
    margin: values.margin,
    extra_value: values.extraValue,
    credits: values.credits,
  };
};

export const ProposalTableRow = ({
  item,
  onRemove,
  onUpdate,
}: {
  item: ProposalItem;
  onRemove: () => void;
  onUpdate: (updatedItem: ProposalItem) => void;
}) => {
  const [rowData, setRowData] = useState<ProposalItem>(calculateValues(item));

  const handleExtraValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const extraPrice = Number(event.target.value);
    const newData = calculateValues(rowData, extraPrice);
    setRowData(newData);
    onUpdate(newData);
  };

  return (
    <TableRow>
      <input type="hidden" name="privateRoomsIds" value={item.id} readOnly />
      <input
        type="hidden"
        name="extraCredits"
        value={`${item.id},${rowData.total_credits}`}
        readOnly
      />
      <TableCell></TableCell>
      <TableCell>
        <div className="flex flex-col">
          {rowData.label}
          <span className="text-gray-500">{rowData.search_id}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col">
          {rowData.space.name}
          <span className="text-gray-500">{rowData.space.id}</span>
        </div>
      </TableCell>
      <TableCell>{rowData.space.neighborhood}</TableCell>
      <TableCell>{rowData.space.cityname}</TableCell>
      <TableCell>{rowData.capacity}</TableCell>
      <TableCell>
        {rowData.size && rowData.size !== null ? `${rowData.size} m2` : "-"}
      </TableCell>
      <TableCell>Mensal: {rowData.credits}</TableCell>
      <TableCell>{formatCurrency(rowData.client_price ?? 0)}</TableCell>
      <TableCell>
        <Input
          id={`extra-value-${item.id}`}
          label=""
          type="number"
          value={rowData.extra_value || ""}
          onChange={handleExtraValueChange}
          className="w-full"
        />
      </TableCell>
      <TableCell>{rowData.extra_credits}</TableCell>
      <TableCell className="font-bold">
        {formatCurrency(rowData.total_price ?? 0)}
      </TableCell>
      <TableCell className="font-bold">{rowData.total_credits}</TableCell>
      <TableCell className="font-bold">{rowData.margin ?? "-"}%</TableCell>
      <TableCell>
        <button
          type="button"
          className="border border-zinc-200 p-2"
          onClick={onRemove}
        >
          <TrashIcon className="text-zinc-950 hover:text-zinc-950 w-4 h-4" />
        </button>
      </TableCell>
    </TableRow>
  );
};

const ProposalTableSkeleton = () => {
  return (
    <Table id="table-proposal" className="w-full caption-bottom text-sm">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[24px]" />
          <TableHead className="text-zinc-600">Sala</TableHead>
          <TableHead className="text-zinc-600">Espaço</TableHead>
          <TableHead className="text-zinc-600">Bairro</TableHead>
          <TableHead className="text-zinc-600">Cidade</TableHead>
          <TableHead className="text-zinc-600">Capacidade</TableHead>
          <TableHead className="text-zinc-600">Tamanho</TableHead>
          <TableHead className="text-zinc-600">Créditos</TableHead>
          <TableHead className="text-zinc-600">Valor do Cliente</TableHead>
          <TableHead className="text-zinc-600">Valor Extra R$</TableHead>
          <TableHead className="text-zinc-600">Créditos Extras</TableHead>
          <TableHead className="text-blue-700 font-medium">Total R$</TableHead>
          <TableHead className="text-blue-700 font-medium">
            Total Créditos
          </TableHead>
          <TableHead className="text-green-700">Margem</TableHead>
          <TableHead className="text-zinc-600">Ações</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 3 }).map((_, index) => (
          <TableRow key={index}>
            <TableCell>
              <Skeleton className="h-6 w-6" />
            </TableCell>
            <TableCell>
              <div className="flex flex-col gap-1">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3 w-20" />
              </div>
            </TableCell>
            <TableCell>
              <div className="flex flex-col gap-1">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3 w-20" />
              </div>
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-10 w-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-10 w-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-10 w-10" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const ProposalTable = ({
  items,
  onRemoveItem,
  onUpdateItem,
}: {
  items: ProposalItem[];
  onRemoveItem: (item: ProposalItem, index: number) => void;
  onUpdateItem: (updatedItem: ProposalItem) => void;
}) => {
  return (
    <Table id="table-proposal" className="w-full caption-bottom text-sm">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[24px]" />
          <TableHead className="text-zinc-600">Sala</TableHead>
          <TableHead className="text-zinc-600">Espaço</TableHead>
          <TableHead className="text-zinc-600">Bairro</TableHead>
          <TableHead className="text-zinc-600">Cidade</TableHead>
          <TableHead className="text-zinc-600">Capacidade</TableHead>
          <TableHead className="text-zinc-600">Tamanho</TableHead>
          <TableHead className="text-zinc-600">Créditos</TableHead>
          <TableHead className="text-zinc-600">Valor do Cliente</TableHead>
          <TableHead className="text-zinc-600">Valor Extra R$</TableHead>
          <TableHead className="text-zinc-600">Créditos Extras</TableHead>
          <TableHead className="text-blue-700 font-medium">Total R$</TableHead>
          <TableHead className="text-blue-700 font-medium">
            Total Créditos
          </TableHead>
          <TableHead className="text-green-700">Margem</TableHead>
          <TableHead className="text-zinc-600">Ações</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item, index) => (
          <ProposalTableRow
            key={item.id}
            item={item}
            onRemove={() => {
              onRemoveItem(item, index);
            }}
            onUpdate={onUpdateItem}
          />
        ))}
      </TableBody>
    </Table>
  );
};

interface Vision360ProposalModalProps {
  open: boolean;
  origin: string;
  parentLongListId?: string | null;
  onClose: () => void;
  onSuccess: (proposalData: {
    proposalLongUrl: string;
    proposalShortUrl: string;
    privateRoomsIds: string[];
  }) => void;
  allCards: Card[];
  dealId?: string;
  filters?: FilterValues;
}

export function Vision360ProposalModal({
  open,
  origin,
  parentLongListId,
  onClose,
  onSuccess,
  allCards,
  dealId,
  filters,
}: Vision360ProposalModalProps) {
  const { userInfo } = useAuth();
  const { selectedIds } = useSelection();
  const [selectedItems, setSelectedItems] = useState<ProposalItem[]>([]);
  const [dealDataValues, setDealDataValues] = useState<{ id: number }>();
  const [dealUrl, setDealUrl] = useState<string>(dealId ?? "");
  const [dealUrlValidation, setDealUrlValidation] = useState<{
    status: boolean;
    message: string;
  }>();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingItems, setIsLoadingItems] = useState(false);

  useEffect(() => {
    setDealUrl(dealId ?? "");
  }, [dealId]);

  const {
    control,
    formState: { isValid },
    handleSubmit,
    setValue,
  } = useForm<ProposalPrivateRoomFormData>({
    resolver: yupResolver(ProposalPrivateRoomSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      deal_url: dealId,
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    const convertCardsToProposalItems = async () => {
      setIsLoadingItems(true);
      try {
        const selectedCards = allCards.filter((card) =>
          selectedIds.has(card.id),
        );
        const items = await Promise.all(
          selectedCards.map(async (card) => {
            const spaceIdNumber = Number(card.space.id);
            const isVirtualSpace = isNaN(spaceIdNumber);

            // Integration in initial state uses virtual spaces - generate numeric ID for API compatibility
            // Format: 111 + search_id (e.g., 111654893118 for search_id 654893118)
            const numericSpaceId = isVirtualSpace
              ? `111${card.search_id || Date.now()}`
              : card.space.id;

            const take = isVirtualSpace
              ? String(DEFAULT_SPACE_TAKE_PCT)
              : await getSpaceTakeValue(spaceIdNumber);
            const baseItem = {
              id: card.id,
              label: card.title,
              search_id: card.search_id ?? null,
              space: {
                id: numericSpaceId,
                name: card.space.name,
                neighborhood: card.neighborhood || "",
                cityname: card.cityname || "",
              },
              capacity: card.capacity,
              size: card.size ?? null,
              monthly_price: card.monthly_price,
              credits: card.credits,
              take: Number(take),
              space_id: isVirtualSpace
                ? numericSpaceId
                : (card.space_id ?? card.space.id),
              private_room_id: card.private_room_id ?? card.id,
              match_percent: card.match_percentage ?? null,
              monthly_credit_price:
                card.monthly_credit_price ?? card.credits ?? null,
              dpp: card.original_url ?? null,
              status: card.status ?? null,
              days_since_update: card.days_since_update ?? null,
              distance_km: card.distance_km ?? null,
              amenities: card.amenities ?? null,
              attributes: card.attributes ?? null,
              details: card.details
                ? {
                    ...card.details,
                    id: card.details.id ?? null,
                  }
                : null,
              space_details: card.space_details
                ? {
                    ...card.space_details,
                    id: isVirtualSpace
                      ? numericSpaceId
                      : (card.space_details.id ?? card.space.id),
                  }
                : null,
              detailed_score_analysis: card.score
                ? card.score.map((scoreItem) => ({
                    criterion: scoreItem.criterion,
                    requested: scoreItem.requested,
                    offered: scoreItem.offered,
                    matched: scoreItem.matched,
                  }))
                : null,
              score_details: card.score_details ?? null,
            };

            return calculateValues(baseItem);
          }),
        );
        setSelectedItems(items);
      } finally {
        setIsLoadingItems(false);
      }
    };

    if (selectedIds.size > 0) {
      convertCardsToProposalItems();
    } else {
      setSelectedItems([]);
      setIsLoadingItems(false);
    }
  }, [open, allCards, selectedIds]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (dealId && dealId.trim() !== "") {
      setValue("deal_url", dealId, { shouldValidate: true });
      getDealUrlResult(dealId);
    }
  }, [open, dealId, setValue]);

  async function verifyDealUrl(dealUrl: string) {
    const splitDealUrl = dealUrl.split("/").filter((n) => n);
    const deal_id = splitDealUrl[splitDealUrl.length - 1];

    if (!deal_id) {
      return {
        status: false,
        message: "Deal inválido",
      };
    }

    return await apiHubspotIntegrationMs
      .get(`/deals/${deal_id}`)
      .then(function (resp) {
        setDealDataValues(resp.data);
        return {
          status: true,
          message: "Deal verificado",
        };
      })
      .catch(function () {
        return {
          status: false,
          message: "Deal inválido",
        };
      });
  }

  function removeItem(item: any) {
    setSelectedItems(
      selectedItems.filter((selectedItem: any) => selectedItem !== item),
    );
  }

  function updateSelectedItem(updatedItem: ProposalItem) {
    setSelectedItems((prevItems) =>
      prevItems.map((item) =>
        item.id === updatedItem.id ? updatedItem : item,
      ),
    );
  }

  const getDealUrlResult = (dealUrl: string) => {
    verifyDealUrl(dealUrl || dealId || "").then((resp) => {
      setDealUrlValidation(resp);
    });
  };

  const onSubmit = async () => {
    if (selectedItems.length === 0) {
      createToast("Nenhuma sala selecionada", {
        type: "error",
        timeout: 2000,
      });
      return;
    }

    setIsLoading(true);

    try {
      const privateRoomsIds = selectedItems
        .map((item) => item.search_id?.toString() || item.id)
        .join(",");
      const extraCredits = selectedItems
        .map(
          (item) => `${item.search_id?.toString()},${item.total_credits || 0}`,
        )
        .join(";");
      const userId = userInfo?.user?.id?.toString() ?? "";
      const dealIdValue = dealId || dealDataValues?.id?.toString() || "";

      const searchParams = new URLSearchParams();
      const hashedCredits = btoa(extraCredits);

      searchParams.append("user_id", userId);
      searchParams.append("deal_id", dealIdValue);

      const proposalUrl = `${import.meta.env.VITE_PORTAL_URL}/proposal/privaterooms/${privateRoomsIds}?ec=${hashedCredits}&${searchParams}`;

      const resultShortUrl = (
        await getBitlyShortUrl.post("", {
          long_url: proposalUrl,
        })
      ).data.link;

      if (!resultShortUrl) {
        createToast("Error generating URL, please try again", {
          type: "error",
          timeout: 2000,
        });
        return;
      }

      sendTrackDataServer({
        eventName: "VISION_CREATE_PROPOSAL_CLICKED",
        data: {
          private_room_ids: selectedItems.map((item) => item.id),
          deal_id: dealId ?? null,
          origin: origin,
          parent_long_list_id: parentLongListId ?? null,
        },
        url: location.pathname,
        productType: "PO",
      });

      await visionService.createLongList({
        credits_price: FIXED_CREDIT_PRICE,
        deal_id: dealIdValue || null,
        extra_value: null,
        items: selectedItems,
        user_id: userId,
        filters: filters,
        origin: origin,
        parent_long_list_id: parentLongListId ?? null,
      });

      if (dealIdValue) {
        visionService.completeTtoMetric(dealIdValue, userId).catch((error) => {
          reportError(error, {
            operation: "completeTtoMetric",
            dealId: dealIdValue,
          });
        });
      }

      onSuccess({
        proposalLongUrl: proposalUrl,
        proposalShortUrl: resultShortUrl,
        privateRoomsIds: selectedItems.map(
          (item) => item.search_id?.toString() || item.id,
        ),
      });
    } catch (error) {
      console.error("Error creating long list", error);
      createToast("Erro ao criar long list", {
        type: "error",
        timeout: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalDialog
      type="submit"
      title="Long List de Sala Privativa"
      description={`Revise as opções selecionadas abaixo, preencha os campos necessários e finalize a criação da long list`}
      open={open}
      handleClose={onClose}
      handleConfirm={() => {}}
      enableButton={isValid && !isLoading}
      maxMidth="max-w-[85rem] p-0"
      showFooter={false}
      titleStyle="text-lg pt-6 px-6 pb-0 zinc-950"
      descriptionStyle="text-sm px-6 text-zinc-500 mt-0"
    >
      <div className="pt-5 pb-6">
        <div className="px-5 mx-1 max-w-[15rem]">
          <label className="text-zinc-600 text-sm">
            Valor do crédito do cliente
          </label>
          <p className="text-sm font-semibold text-zinc-950">
            {formatCurrency(FIXED_CREDIT_PRICE)}
          </p>
          <span className="text-xs text-zinc-500">
            Valor fixo definido pela política comercial
          </span>
        </div>
        <div className="px-5 mx-1 mt-5">
          <label className="text-zinc-600 text-sm">Salas Selecionadas</label>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input hidden name="userId" value={userInfo?.user?.id} readOnly />
          <input hidden name="dealId" value={dealDataValues?.id} readOnly />

          <div className="flex flex-col gap-6 table-height-screen">
            <div className="relative w-full overflow-auto">
              {isLoadingItems ? (
                <ProposalTableSkeleton />
              ) : (
                <ProposalTable
                  items={selectedItems}
                  onRemoveItem={removeItem}
                  onUpdateItem={updateSelectedItem}
                />
              )}
            </div>
            <div className="flex flex-col px-6">
              <div className="flex gap-3 items-end">
                <a
                  className="flex gap-1 text-blue-800 text-xs items-center underline"
                  href="https://www.notion.so/wobabr/Gera-o-de-propostas-para-contrata-o-de-Salas-Privativas-4a59ce29d5334a87898466b2711de2aa?pvs=4"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Info className="w-4 h-4" />
                  Verificar instruções para geração de long list
                </a>
              </div>
            </div>
            <div className="flex flex-col px-6">
              <div className="flex flex-col">
                <div className="flex gap-3 items-end">
                  <div className="flex-initial w-full">
                    <Controller
                      control={control}
                      name="deal_url"
                      render={({ field, fieldState }) => (
                        <Input
                          className="w-full"
                          label="Deal ID"
                          name="deal_url"
                          type="string"
                          showlabel={true}
                          value={field.value || dealId || ""}
                          onChange={(event) => {
                            field.onChange(event);
                            setDealUrl(event.target.value);
                          }}
                          error={
                            fieldState.error ? fieldState.error.message : ""
                          }
                        />
                      )}
                    />
                  </div>
                  <Button
                    className="txt-sm py-2 px-4 flex-initial w-32 min-h-10"
                    onClick={() => {
                      getDealUrlResult(dealUrl!);
                    }}
                    disabled={!isValid}
                    type="button"
                  >
                    Verificar Deal
                  </Button>
                </div>
                {dealUrlValidation?.message && (
                  <div
                    className={`text-sm ${dealUrlValidation?.status ? "text-lime-500" : "text-red-500"} pt-2`}
                  >
                    {dealUrlValidation?.message}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="mt-6 border-t pt-6 flex gap-3 justify-end items-center px-6">
            <Button
              className="txt-sm bg-white border border-zinc-200 box-shadow-none text-black text-sm hover:bg-gray-100 py-2 px-4 min-h-10"
              onClick={onClose}
              type="button"
            >
              Cancelar
            </Button>

            <Button
              className="txt-sm py-2 px-4 min-h-10"
              type="submit"
              disabled={!isValid || isLoading}
            >
              {isLoading ? "Criando..." : "Confirmar"}
            </Button>
          </div>
        </form>
      </div>
    </ModalDialog>
  );
}
